/**
 * MetaPage Component
 * Main page for Meta Pixel integration
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthToken } from '../hooks/useAuthToken';
import useMetaStatus from '../hooks/useMetaStatus';
import ConnectMeta from '../components/meta/ConnectMeta';
import PixelVerify from '../components/meta/PixelVerify';
import MetaDashboard from '../components/meta/MetaDashboard';
import Toast, { useToast } from '../components/analytics/Toast';
import '../styles/meta-variables.css';
import './MetaPage.css';

// TODO: Store frontend integration
// After pixelId is saved to StoreSeo.metaPixelId via POST /api/meta/pixel/save,
// the store frontend must read it from the business/info API response
// and inject the full Meta Pixel script into the store <head>.

const MetaPage = () => {
  const navigate = useNavigate();
  const token = useAuthToken();
  const { status, metaInfo, error, refetch } = useMetaStatus();
  const { toasts } = useToast();

  const handleBack = () => navigate('/analytics-hub');

  // ── Loading ───────────────────────────────────────────
  if (!token || status === 'loading') {
    return (
      <div className="meta-page-wrapper">
        <div className="meta-page-header">
          <button className="meta-back-btn" onClick={handleBack}>
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
            </svg>
          </button>
          <div className="meta-page-title-block">
            <svg className="meta-page-title-icon" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            <h1 className="meta-page-title">Meta Pixel</h1>
          </div>
        </div>
        <div className="meta-page-content">
          <div className="meta-page-loading">
            <div className="meta-spinner meta-spinner-lg" />
          </div>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="meta-page-wrapper">
        <div className="meta-page-header">
          <button className="meta-back-btn" onClick={handleBack}>
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
            </svg>
          </button>
          <div className="meta-page-title-block">
            <svg className="meta-page-title-icon" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            <h1 className="meta-page-title">Meta Pixel</h1>
          </div>
        </div>
        <div className="meta-page-content">
          <div className="meta-page-error">
            <div className="meta-page-error-icon">
              <svg viewBox="0 0 24 24">
                <path fill="currentColor" d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
              </svg>
            </div>
            <h2 className="meta-page-error-title">Failed to load Meta Pixel</h2>
            <p className="meta-page-error-text">{error || 'An error occurred'}</p>
            <button className="meta-btn-retry" onClick={refetch}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Content ──────────────────────────────────────
  return (
    <div className="meta-page-wrapper">
      <div className="meta-page-header">
        <button className="meta-back-btn" onClick={handleBack}>
          <svg viewBox="0 0 24 24">
            <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
          </svg>
        </button>
        <div className="meta-page-title-block">
          <svg className="meta-page-title-icon" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <h1 className="meta-page-title">Meta Pixel</h1>
        </div>
      </div>

      <div className="meta-page-content">

        {/* Step 1 — Not connected yet → Show ConnectMeta */}
        {status === 'disconnected' && (
          <ConnectMeta onConnect={refetch} />  
        )}

        {/* Step 2 — Connected but not verified → Show PixelVerify */}
        {status === 'connected' && !metaInfo?.isVerified && (
          <PixelVerify
            metaInfo={metaInfo}
            onVerified={refetch}
            onDisconnect={refetch}
          />
        )}

        {/* Step 3 — Connected and verified → Show Dashboard */}
        {status === 'connected' && metaInfo?.isVerified && (
          <MetaDashboard
            metaInfo={metaInfo}
            onDisconnect={refetch}
          />
        )}

      </div>

      <Toast toasts={toasts} />
    </div>
  );
};

export default MetaPage;